import { previewObjectivesAction, type AnalyticsEvent } from '../../core/events';
import { useId, useRef, useState } from 'react';
import { TEACHER_LIMITS, type QuotaLimits } from '../../core/quota';
import { MAX_UPLOAD_BYTES, decideUpload } from '../../core/upload';

/**
 * Taking the plan in. The same `decideUpload` the Worker runs, so "too large" means one
 * thing across both sides by construction rather than by two implementations happening to
 * agree — and the teacher hears about it before the bytes go anywhere.
 */

export interface UploaderProps {
  /** False while the flags resolve: inert, but present. A layout jump on the CTA is worse. */
  disabled?: boolean;
  /** The upload gate is closed — the tool is read-only right now. */
  gated?: boolean;
  /**
   * The caller's own ceilings, from `/api/usage`. A 240 account gets a raised pair, so this
   * copy cannot be a constant — it would tell exactly the people with the higher allowance
   * that they have five.
   */
  limits?: QuotaLimits;
  busy?: boolean;
  error?: string | null;
  onUpload: (file: File) => void;
  onRejected: (reason: string) => void;
  onTrack?: (event: AnalyticsEvent) => void;
}

const REJECTION_COPY: Record<string, string> = {
  empty: 'That file is empty.',
  too_large: `That file is over ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB. Try a single lesson plan rather than a whole unit.`,
  unsupported_type: 'Upload a PDF, a Word document, or a Markdown or text file.',
  content_mismatch:
    'That file does not match its type — a PDF that is not really a PDF, or text that is not UTF-8. Re-export it and try again.',
};

export default function Uploader({
  disabled = false,
  gated = false,
  limits = TEACHER_LIMITS,
  busy = false,
  error,
  onUpload,
  onRejected,
  onTrack,
}: UploaderProps) {
  const [chosen, setChosen] = useState<File | null>(null);
  const [message, setMessage] = useState('');
  const inputId = useId();
  const messageId = useId();
  const input = useRef<HTMLInputElement>(null);

  if (gated) {
    return (
      <div className="panel notice" role="status">
        <h2>Uploads are paused.</h2>
        <p>Reports you have already run are still here. Check back shortly.</p>
      </div>
    );
  }

  function choose(file: File | null) {
    setChosen(file);
    setMessage('');
    if (!file) {
      onTrack?.(previewObjectivesAction('file_selected', { selected: false }));
      return;
    }

    const decision = decideUpload({
      contentType: file.type,
      filename: file.name,
      byteLength: file.size,
    });

    if (!decision.ok) {
      setMessage(REJECTION_COPY[decision.reason] ?? 'That file cannot be read.');
      onRejected(decision.reason);
      setChosen(null);
      if (input.current) input.current.value = '';
    } else {
      onTrack?.(previewObjectivesAction('file_selected', { selected: true, kind: decision.kind, byte_length: file.size }));
    }
  }

  return (
    <form
      className="panel"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || disabled || !chosen) return;
        onUpload(chosen);
      }}
      noValidate
    >
      <h2>Upload a lesson plan</h2>
      <p className="hint">
        Upload one lesson plan as a PDF, Word document, Markdown file, or text file.
        We’ll identify its objectives and show which standards they align with.
      </p>

      {/* Uploaded content can contain personal information; this warning is not redaction. */}
      <p className="warn">
        {limits.uploads} uploads and {limits.analyses} analyses per account every seven days. Processing attempts count toward the limit.<br />
        <strong>Before you upload</strong><br />
        Remove student names, student work, and other identifying information.
      </p>

      <div className="field-row">
        <label htmlFor={inputId}>Lesson plan</label>
        <input
          id={inputId}
          ref={input}
          type="file"
          className="ph-no-capture"
          accept=".pdf,.doc,.docx,.md,.markdown,.txt,.html,application/pdf,text/markdown,text/plain"
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
          disabled={disabled || busy}
        />
      </div>

      <details className="retention-details">
        <summary>What we keep</summary>
        <p className="hint">
          We delete the original file when processing begins and don’t save its full text.
          Your report retains objectives and short excerpts until you delete it.
          To run another analysis, upload the file again.
        </p>
      </details>

      <p
        className="message"
        id={messageId}
        role="status"
        aria-live="polite"
        data-tone={message || error ? 'error' : undefined}
      >
        {message || error || (busy ? 'Processing your lesson plan. This may take a moment.' : '')}
      </p>

      <button className="primary process-button" type="submit" aria-busy={busy} disabled={disabled || busy || !chosen}>
        {busy && <span className="processing-spinner" aria-hidden="true" />}
        {busy ? 'Processing…' : 'Process this plan'}
      </button>
    </form>
  );
}
